import { useCallback, useEffect, useRef, useState } from 'react';
import { SEARCH_PAUSE_MS } from './searchPause';

/**
 * Dropdown / Autocomplete (piece 3): the search runs against the application's
 * own records on the server, debounced 250ms, two characters minimum, and the
 * in-flight request is cancelled when the next keystroke fires. Rows are two
 * lines, and every list ends with the Add New X row.
 */
export interface SuggestionRow {
  id: string;
  line1: string;
  line2?: string;
}

export type SearchFn = (query: string, signal: AbortSignal) => Promise<SuggestionRow[]>;

export type DropdownStatus = 'idle' | 'loading' | 'ready';

/** 🔨 An empty field lists what there is and typing filters it (decision 0268); the two-character minimum stood until 2026-09-10. */
export const DROPDOWN_MIN_CHARS = 0;

/**
 * A search over a list the page already holds (T307): a short set of records
 * read once, such as the knowledge base's categories, filtered in the browser.
 * Every typed word has to appear in a row's first or second line, whatever
 * its case. A pinned row, such as the field's empty choice, leads every
 * answer whatever was typed, so a picked record can always be taken back.
 */
export function searchWithin(rows: readonly SuggestionRow[], pinned: readonly SuggestionRow[] = []): SearchFn {
  return (query) => {
    const words = query.toLowerCase().split(/\s+/).filter(Boolean);
    const matched = rows.filter((row) => {
      const text = `${row.line1} ${row.line2 ?? ''}`.toLowerCase();
      return words.every((word) => text.includes(word));
    });
    return Promise.resolve([...pinned, ...matched]);
  };
}

/** The Add New row follows the last suggestion; the highlight can rest on it. */
export const ADD_NEW = 'addNew' as const;

export type DropdownKey = 'ArrowDown' | 'ArrowUp' | 'Enter' | 'Tab' | 'Escape';

export interface KeyOutcome {
  select?: SuggestionRow | typeof ADD_NEW;
  close?: boolean;
}

/**
 * Move the highlight over the rows plus the Add New row at index rowCount; -1 is no highlight.
 * 🔒 A list that withholds Add New (decision 1704) stops at its last suggestion.
 */
export function moveHighlight(current: number, rowCount: number, direction: 1 | -1, withAddNew = true): number {
  const last = withAddNew ? rowCount : rowCount - 1;
  if (last < 0) return -1;
  if (current < 0) return direction === 1 ? 0 : last;
  const next = current + direction;
  if (next < 0) return last;
  if (next > last) return 0;
  return next;
}

export interface DropdownSearchOptions {
  search: SearchFn;
  debounceMs?: number;
  minChars?: number;
  /** 🔒 False where creating the entity from the field would make one the field refuses (decision 1704); true otherwise. */
  withAddNew?: boolean;
}

export function useDropdownSearch({ search, debounceMs = SEARCH_PAUSE_MS, minChars = DROPDOWN_MIN_CHARS, withAddNew = true }: DropdownSearchOptions) {
  const [query, setQueryState] = useState('');
  const [rows, setRows] = useState<SuggestionRow[]>([]);
  const [status, setStatus] = useState<DropdownStatus>('idle');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlight = useRef<AbortController | null>(null);

  const cancel = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    if (inFlight.current) inFlight.current.abort();
    inFlight.current = null;
  }, []);

  useEffect(() => cancel, [cancel]);

  const setQuery = useCallback(
    (next: string) => {
      setQueryState(next);
      setOpen(true);
      setHighlight(-1);
      cancel();
      if (next.trim().length < minChars) {
        setRows([]);
        setStatus('idle');
        return;
      }
      timer.current = setTimeout(() => {
        const controller = new AbortController();
        inFlight.current = controller;
        setStatus('loading');
        search(next, controller.signal).then(
          (result) => {
            if (controller.signal.aborted) return;
            setRows(result);
            setStatus('ready');
          },
          () => {
            if (controller.signal.aborted) return;
            setRows([]);
            setStatus('ready');
          },
        );
      }, debounceMs);
    },
    [cancel, debounceMs, minChars, search],
  );

  const close = useCallback(() => {
    setOpen(false);
    setHighlight(-1);
  }, []);

  /** Show text in the field without searching or opening: the selection's first line, or a value the page holds. */
  const showText = useCallback(
    (text: string) => {
      cancel();
      setQueryState(text);
      setRows([]);
      setStatus('idle');
      setOpen(false);
      setHighlight(-1);
    },
    [cancel],
  );

  /** A chosen row: the field shows its first line and the list closes. */
  const select = useCallback((row: SuggestionRow) => showText(row.line1), [showText]);

  const onKey = useCallback(
    (key: DropdownKey): KeyOutcome => {
      switch (key) {
        case 'ArrowDown':
          setOpen(true);
          setHighlight((h) => moveHighlight(h, rows.length, 1, withAddNew));
          return {};
        case 'ArrowUp':
          setHighlight((h) => moveHighlight(h, rows.length, -1, withAddNew));
          return {};
        case 'Escape':
          close();
          return { close: true };
        case 'Enter':
        case 'Tab': {
          if (!open || highlight < 0) return {};
          const chosen = highlight === rows.length ? (withAddNew ? ADD_NEW : undefined) : rows[highlight];
          close();
          return chosen ? { select: chosen, close: true } : {};
        }
      }
    },
    [rows, open, highlight, close, withAddNew],
  );

  return { query, setQuery, showText, select, rows, status, open, setOpen, highlight, setHighlight, onKey, close };
}
